import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useState } from 'react'
import { BpButton } from '../../../../shared/components/buttons/bp-button/BpButton'
import { Controller, useForm } from 'react-hook-form'
import { BpInput } from '../../../../shared/components/forms/input/BpInput'
import { BpSelect } from '../../../../shared/components/forms/select/BpSelect'
import { BpTextArea } from '../../../../shared/components/forms/textarea/BpTextArea'
import { BpCheckbox } from '../../../../shared/components/forms/checkbox/BpCheckbox'
import { IOption } from '../../../../shared/components/forms/search-input/BpSearchInput'
import { parseDomainList } from '../../../../shared/lib/helpers/ExtractHostname'
import { Preloader } from '../../../../shared/components/cards/preloader/Preloader'
import {
  GetFilterCityApiArg,
  useGetFilterBrowserLanguageQuery,
  useGetFilterCityQuery,
  useGetFilterCountryQuery,
  useGetFilterDeviceTypesQuery,
  useGetFilterEngineQuery,
  useGetProjectLocationsDefaultSettingsQuery,
  usePostProjectCreateMutation,
} from '../../../../app/api'
import { useAppDispatch, useAppSelector } from '../../../../app/store/hooks/Redux'
import { hideModal, setModal, showAlert } from '../../../../app/store/reducers/AppSlice'
import { useTranslation } from 'react-i18next'
import {
  selectTariffDetails,
  selectUser,
  setTariffUpdated,
} from '../../../../app/store/reducers/AuthSlice'
import { CardDrop } from '../../../../shared/components/cards/card-drop/CardDrop'
import { useAiChat } from '../../../../shared/components/ai/ai-provider/AiProvider'
import { AiButton } from '../../../../shared/components/ai/ai-button/AiButton'
import { TariffDetailsModal } from '../tariff-details-modal/TariffDetailsModal'
import { ALL_CITIES_VALUE } from '../../../../shared/consts/AllCities.const'
import clsx from 'clsx'
import styles from './AddProjectModal.module.scss'
import { renderCountryOption } from '../../../../shared/components/utility/country-option/CountryOption'

export interface AddProjectRef {
  hasUnsavedChanges: () => boolean
}

export const AddProjectModal = forwardRef<AddProjectRef>((_, ref) => {
  const { t } = useTranslation()
  const { handleSendMessage } = useAiChat()
  const { id: userId, company_id: companyId } = useAppSelector(selectUser)
  const tariffDetails = useAppSelector(selectTariffDetails)
  const [step, setStep] = useState(1)
  const [disabled, setDisabled] = useState(true)
  const dispatch = useAppDispatch()

  const firstStepForm = useForm<{
    project_name: string
    brand_name: string
    brand_variants: string
    domains: string
    engines: boolean[]
    devices: boolean[]
  }>({
    mode: 'onChange',
    defaultValues: {
      project_name: '',
      brand_name: '',
      brand_variants: '',
      domains: '',
      engines: [false, true, false],
      devices: [true, true],
    },
  })

  const secondStepForm = useForm<{
    country: string
    cities: string[]
    browser_language: string
  }>({
    mode: 'onChange',
    defaultValues: {
      country: '',
      cities: [],
      browser_language: '',
    },
  })

  const thirdStepForm = useForm<{
    terms: string
  }>({
    mode: 'onChange',
    defaultValues: {
      terms: '',
    },
  })

  const [createProject, { isLoading: createProjectLoading }] = usePostProjectCreateMutation()

  const {
    control: firstStepControl,
    register: firstStepRegister,
    watch: firstStepWatch,
    setValue: firstStepSetValue,
    setError: firstStepSetError,
    formState: { errors: firstStepErrors, isValid: firstStepValid },
  } = firstStepForm

  const {
    control: secondStepControl,
    register: secondStepRegister,
    watch: secondStepWatch,
    setValue: secondStepSetValue,
    setError: secondStepSetError,
    formState: { errors: secondStepErrors, isValid: secondStepValid },
  } = secondStepForm

  const {
    control: thirdStepControl,
    register: thirdStepRegister,
    setValue: thirdStepSetValue,
    watch: thirdStepWatch,
  } = thirdStepForm

  const firstStepValues = firstStepWatch()
  const secondStepValues = secondStepWatch()
  const thirdStepValues = thirdStepWatch()

  const {
    data: countries,
    isFetching: countriesFetching,
    isLoading: countriesLoading,
  } = useGetFilterCountryQuery({})
  const { data: locationsDefaultSettings } = useGetProjectLocationsDefaultSettingsQuery(
    {
      countryIds: [Number(secondStepValues?.country)],
    },
    { skip: !secondStepValues?.country },
  )

  const { data: cities } = useGetFilterCityQuery(
    {
      all: true,
      countryId: Number(secondStepValues?.country),
    } as unknown as GetFilterCityApiArg,
    { skip: !secondStepValues?.country },
  )

  const { data: engines, isLoading: enginesLoading } = useGetFilterEngineQuery({})

  const { data: devices, isLoading: devicesLoading } = useGetFilterDeviceTypesQuery()

  const { data: browserLanguages } = useGetFilterBrowserLanguageQuery(
    {
      countryId: Number(secondStepValues?.country),
    },
    { skip: !secondStepValues?.country },
  )

  const filteredCountries = useMemo(() => {
    const currentEngines = firstStepValues.engines
      .map((e, i) => (e ? engines?.[i] : undefined))
      .filter(Boolean)

    if (currentEngines.length > 1) return countries

    const engine = currentEngines[0]

    return engine?.allowed_country_ids?.length
      ? countries?.filter((c) => engine.allowed_country_ids?.includes(Number(c.value)))
      : countries
  }, [countries, engines, firstStepValues])

  useEffect(() => {
    const first =
      !firstStepValid ||
      !!domainsValidate(firstStepValues?.domains).length ||
      !firstStepValues?.engines.filter((engine) => engine).length ||
      !firstStepValues?.devices.filter((device) => device).length
    const second =
      !secondStepValid ||
      !secondStepValues?.cities.length ||
      !secondStepValues?.browser_language.length
    const third =
      !thirdStepValues.terms.length ||
      !/.*\S+.*/.test(thirdStepValues.terms) ||
      (tariffDetails.tariff &&
        tariffDetails.tariff.max_keywords <
          tariffDetails.tariff.used_keywords_count + thirdStepValues.terms.split('\n').length)

    setDisabled(
      {
        1: first,
        2: second,
        3: third,
      }[step] || false,
    )
  }, [firstStepValues, secondStepValues, thirdStepValues])

  useEffect(() => {
    if (locationsDefaultSettings?.data && !secondStepValues.browser_language) {
      setTimeout(
        () =>
          secondStepSetValue(
            'browser_language',
            locationsDefaultSettings.data?.items?.[0]?.languages[0]?.id ?? '',
          ),
        250,
      )
    }
  }, [locationsDefaultSettings])

  useEffect(() => {
    firstStepValues.engines.forEach((selected, i) => {
      const engine = selected ? engines?.[i] : undefined
      const allowed = engine?.allowed_country_ids

      if (allowed?.length && !allowed.includes(Number(secondStepValues.country))) {
        firstStepSetValue(
          'engines',
          firstStepValues.engines.map((v, j) => (i === j ? false : v)),
        )
      }
    })
  }, [secondStepValues.country, engines, firstStepValues.engines, firstStepSetValue])

  useEffect(() => {
    secondStepSetValue('cities', cities?.length ? [ALL_CITIES_VALUE] : [])
  }, [cities])

  const domainsValidate = (list: string): string[] => {
    const domainRegex = /^(?:https?:\/\/)?([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}(:\d+)?(\/.*)?$/

    return list
      .split('\n')
      .map((line: string) => line.trim())
      .filter((line: string) => line && !domainRegex.test(line))
  }

  const hasUnsavedChanges = (): boolean => {
    const defaultEngines = [false, true, false]
    const defaultDevices = [true, true]

    const hasFirstStepData =
      !!firstStepValues.project_name.trim() ||
      !!firstStepValues.brand_name.trim() ||
      !!firstStepValues.brand_variants.trim() ||
      !!firstStepValues.domains.trim() ||
      firstStepValues.engines.some((e, i) => e !== defaultEngines[i]) ||
      firstStepValues.devices.some((d, i) => d !== defaultDevices[i])

    const hasSecondStepData =
      !!secondStepValues.country ||
      secondStepValues.cities.length > 0 ||
      !!secondStepValues.browser_language

    const hasThirdStepData = thirdStepValues.terms.trim().length > 0

    return hasFirstStepData || hasSecondStepData || hasThirdStepData
  }

  useImperativeHandle(ref, () => ({
    hasUnsavedChanges,
  }))

  const submit = () => {
    const values = { ...firstStepValues, ...secondStepValues, ...thirdStepValues }

    createProject({
      body: {
        step: ({ 1: 'first', 2: 'second', 3: 'third' } as const)[step as 1 | 2 | 3],
        project_name: values.project_name,
        brand_name: values.brand_name,
        brand_variants: values?.brand_variants?.split('\n'),
        domains: parseDomainList(values?.domains ?? ''),
        engines: engines
          ?.filter((_, index) => values?.engines[index])
          .map((engine) => ({ id: Number(engine.value) })),
        devices: devices
          ?.filter((_, index) => values?.devices[index])
          .map((device) => ({ id: Number(device.value) })),
        locations: [
          {
            country_id: Number(values?.country),
            languages: [{ id: values?.browser_language }],
            cities: values?.cities?.map((city) => ({ id: Number(city) })),
            engines: engines
              ?.filter((_, index) => values?.engines[index])
              .map((engine) => ({ id: Number(engine.value) })),
          },
        ],
        terms: {
          country_id: Number(values?.country),
          terms: values?.terms?.split('\n'),
          language_code: values?.browser_language,
        },
      },
    }).then((response: any) => {
      if (step < 3) {
        setStep(step + 1)
      } else {
        if (response.data.success) {
          window.dataLayer = window.dataLayer || []
          window.dataLayer.push({
            event: 'project_created',
            properties: {
              user_id: userId,
              project_id: response.data.data.id,
              project_name: values.project_name,
              company_id: companyId,
            },
          })

          dispatch(setTariffUpdated())
          dispatch(hideModal())
          dispatch(
            showAlert({
              message: t('pages.projects.projectCreatedSuccessfully'),
              type: 'success',
              timeout: 2000,
            }),
          )
        }
      }
    })
  }

  return (
    <div
      className={clsx(
        styles.root,
        step === 1 && styles.overflowHidden,
        (enginesLoading || devicesLoading) && styles.loaderInvisible,
      )}
    >
      <div
        className={clsx(styles.loaderWrap, !(enginesLoading || devicesLoading) && styles.hidden)}
      >
        <Preloader />
      </div>
      <p className={styles.stepLabel}>{t('pages.projects.stepOfSteps', { step, steps: 3 })}</p>
      <form className={clsx(styles.formStep, step !== 1 && styles.formStepHidden)}>
        <h3 className={styles.title}>{t('pages.projects.newProject')}</h3>
        <div className={styles.body}>
          <Controller
            render={({ field }) => (
              <BpInput
                {...field}
                required
                error={firstStepErrors.project_name?.message}
                label={t('fields.projectName')}
                placeholder={t('pages.projects.placeholders.projectName')}
                onClear={() =>
                  firstStepSetValue('project_name', '', {
                    shouldValidate: true,
                  })
                }
                {...firstStepRegister('project_name', {
                  onBlur: (e) => firstStepSetValue('project_name', e.target.value.trim()),
                  required: t('fields.validate.required'),
                  maxLength: {
                    value: 100,
                    message: t('pages.projects.validate.projectName'),
                  },
                  pattern: /.*\S+.*/,
                })}
              />
            )}
            control={firstStepControl}
            name='project_name'
          />
          <Controller
            render={({ field }) => (
              <BpInput
                {...field}
                required
                error={firstStepErrors.brand_name?.message}
                label={t('fields.brandName')}
                placeholder={t('pages.projects.placeholders.brandName')}
                onClear={() =>
                  firstStepSetValue('brand_name', '', {
                    shouldValidate: true,
                  })
                }
                {...firstStepRegister('brand_name', {
                  onBlur: (e) => firstStepSetValue('brand_name', e.target.value.trim()),
                  required: t('fields.validate.required'),
                  maxLength: {
                    value: 100,
                    message: t('pages.projects.validate.brandName'),
                  },
                  pattern: /.*\S+.*/,
                })}
              />
            )}
            control={firstStepControl}
            name='brand_name'
          />
          <Controller
            render={({ field }) => (
              <BpTextArea
                {...field}
                required
                rightSection={
                  <AiButton
                    disabled={!firstStepValues.brand_name}
                    size={'sm'}
                    variant={'outlined'}
                    onClick={() =>
                      handleSendMessage({
                        message: `Generate typos for my brand ${firstStepValues.brand_name}`,
                      })
                    }
                  >
                    {t('buttons.createWithAI')}
                  </AiButton>
                }
                error={firstStepErrors.brand_variants?.message}
                height='lg'
                info={t('pages.projects.info.brandsMisspellingsVariations')}
                label={t('fields.brandsMisspellingsVariations')}
                placeholder={t('pages.projects.placeholders.brandsMisspellingsVariations')}
                {...firstStepRegister('brand_variants', {
                  required: t('fields.validate.required'),
                  pattern: /.*\S+.*/,
                  onChange: (e) => {
                    firstStepSetValue('brand_variants', e.target.value.replace(',', '\n'))
                  },
                  onBlur: (e) => {
                    firstStepSetValue(
                      'brand_variants',
                      e.target.value
                        .split('\n')
                        .map((value: string) => value.trim())
                        .join('\n'),
                    )
                  },
                })}
              />
            )}
            control={firstStepControl}
            name='brand_variants'
          />
          <Controller
            render={({ field }) => (
              <BpTextArea
                {...field}
                required
                error={firstStepErrors.domains?.message}
                height='lg'
                info={t('pages.projects.info.domains')}
                label={t('fields.domains')}
                placeholder={t('pages.projects.placeholders.domains')}
                {...firstStepRegister('domains', {
                  required: t('fields.validate.required'),
                  pattern: /.*\S+.*/,
                  onChange: (e) => {
                    firstStepSetValue(
                      'domains',
                      e.target.value.replace(',', '\n').replace(/[^a-zA-Zа-яА-Я0-9\s.,-:/]/g, ''),
                    )
                  },
                  onBlur: (e) => {
                    firstStepSetValue(
                      'domains',
                      e.target.value
                        .split('\n')
                        .map((value: string) => value.trim())
                        .join('\n'),
                    )

                    if (domainsValidate(e.target.value).length > 0) {
                      firstStepSetError('domains', { message: t('fields.validate.domain') })
                    }
                  },
                })}
              />
            )}
            control={firstStepControl}
            name='domains'
          />
          <div>
            <div className={styles.fieldLabel}>
              {t('fields.selectSearchEnginesForScanning')}
              <span> *</span>
            </div>
            <div className={styles.checkboxRow}>
              {engines?.map((engine, index) => {
                const fieldName = `engines.${index}` as const
                return (
                  <Controller
                    key={index}
                    render={({ field: { value, ...fieldProps } }) => (
                      <BpCheckbox
                        {...fieldProps}
                        disabled={
                          !!engine.allowed_country_ids?.length &&
                          !!secondStepValues.country &&
                          !engine.allowed_country_ids.includes(Number(secondStepValues.country))
                        }
                        checked={value}
                        defaultChecked={false}
                        label={engine.text}
                      />
                    )}
                    control={firstStepControl}
                    name={fieldName}
                  />
                )
              })}
            </div>
          </div>
          <div>
            <div className={styles.fieldLabel}>
              {t('fields.selectDevicesForScanning')}
              <span> *</span>
            </div>
            <div className={styles.checkboxRow}>
              {devices?.map((device, index) => {
                const fieldName = `devices.${index}` as const
                return (
                  <Controller
                    key={index}
                    render={({ field: { value, ...fieldProps } }) => (
                      <BpCheckbox
                        {...fieldProps}
                        checked={value}
                        defaultChecked={false}
                        label={t('fields.options.' + device.text)}
                      />
                    )}
                    control={firstStepControl}
                    name={fieldName}
                  />
                )
              })}
            </div>
          </div>
        </div>
      </form>
      <form
        className={clsx(
          styles.formStep,
          step !== 2 && styles.formStepHidden,
          '!overflow-y-visible',
        )}
      >
        <h3 className={styles.title}>{t('pages.projects.location')}</h3>
        <div className={styles.step2Body}>
          <p className={styles.countryHint}>{t('pages.projects.selectOneCountry')}</p>
          <Controller
            render={({ field }) => (
              <BpSelect
                dropPosition={'auto'}
                {...field}
                required
                disabled={!countries?.length}
                error={secondStepErrors.country?.message}
                label={t('fields.country')}
                loading={countriesLoading || countriesFetching}
                optionRender={renderCountryOption}
                options={filteredCountries}
                placeholder={t('fields.selectCountry')}
                {...secondStepRegister('country', {
                  required: t('fields.validate.required'),
                  onChange: (e) => {
                    secondStepSetValue('country', e.target.value)
                  },
                })}
              />
            )}
            control={secondStepControl}
            name='country'
          />
          <Controller
            render={({ field: { onChange, ...fieldProps } }) => (
              <BpSelect
                dropPosition={'auto'}
                {...fieldProps}
                multiple
                required
                disabled={!cities?.length}
                error={secondStepErrors.cities?.message}
                label={t('fields.cities')}
                options={cities}
                placeholder={t('fields.selectCity')}
                onChange={(e) => {
                  const selected = [].slice
                    .call(e.target.selectedOptions)
                    .map((o: IOption) => o.value)
                  const hasAllCities = selected.includes(ALL_CITIES_VALUE)
                  const hadAllCities = !!fieldProps.value?.includes(ALL_CITIES_VALUE)
                  // «All cities» вытесняет остальные города, выбор города — саму опцию
                  const value: string[] =
                    hasAllCities && (!hadAllCities || selected.length === 1)
                      ? [ALL_CITIES_VALUE]
                      : selected.filter((id: string) => id !== ALL_CITIES_VALUE)

                  value.length && onChange(value)

                  !value.length &&
                    secondStepSetError('cities', {
                      message: t('fields.validate.required'),
                      type: 'required',
                    })
                }}
              />
            )}
            control={secondStepControl}
            name='cities'
          />
          <Controller
            render={({ field }) => (
              <BpSelect
                dropPosition={'auto'}
                {...field}
                required
                disabled={!browserLanguages?.length}
                error={secondStepErrors.browser_language?.message}
                label={t('fields.browserLanguage')}
                options={browserLanguages}
                placeholder={t('fields.selectLanguage')}
                {...secondStepRegister('browser_language', {
                  required: t('fields.validate.required'),
                })}
              />
            )}
            control={secondStepControl}
            name='browser_language'
          />
        </div>
      </form>
      <form className={clsx(styles.formStep, step !== 3 && styles.formStepHidden)}>
        <h3 className={styles.title}>{t('pages.projects.searchTerms')}</h3>
        <div className={styles.step2Body}>
          <Controller
            render={({ field }) => (
              <BpTextArea
                {...field}
                required
                label={`${
                  countries?.find((country) => country.value === secondStepValues?.country)?.text
                }, ${
                  browserLanguages?.find(
                    (language) => language.value === secondStepValues?.browser_language,
                  )?.text
                }${
                  thirdStepValues?.terms.length
                    ? `: ${thirdStepValues?.terms.split('\n').filter((term: string) => !!term.trim().length).length}`
                    : ''
                }`}
                rightSection={
                  <AiButton
                    size={'sm'}
                    variant={'outlined'}
                    onClick={() =>
                      handleSendMessage({
                        message: `Generate keywords for my brand ${
                          firstStepValues.brand_name
                        }, Country ${
                          countries?.find((country) => country.value === secondStepValues?.country)
                            ?.text
                        }, Language ${
                          browserLanguages?.find(
                            (language) => language.value === secondStepValues?.browser_language,
                          )?.text
                        }`,
                      })
                    }
                  >
                    {t('buttons.createWithAI')}
                  </AiButton>
                }
                height='lg'
                placeholder={t('pages.projects.placeholders.terms')}
                {...thirdStepRegister('terms', {
                  required: t('fields.validate.required'),
                  onChange: (e) => {
                    const value = e.target.value.replace(',', '\n').split('\n')

                    if (value.find((string: string) => string.length > 50)) {
                      thirdStepSetValue('terms', thirdStepValues.terms)
                    } else {
                      thirdStepSetValue('terms', value.join('\n'))
                    }
                  },
                  onBlur: (e) => {
                    thirdStepSetValue(
                      'terms',
                      e.target.value
                        .replace(',', '\n')
                        .split('\n')
                        .filter((term: string) => !!term.trim().length)
                        .join('\n'),
                    )
                  },
                })}
              />
            )}
            control={thirdStepControl}
            name={'terms'}
          />
          {tariffDetails.tariff &&
            tariffDetails.tariff.max_keywords <
              tariffDetails.tariff.used_keywords_count +
                thirdStepValues.terms.split('\n').length && (
              <div className={styles.limitBanner}>
                <p className={styles.limitBannerText}>
                  {t('pages.tariff.limitExceeded', { tariff: tariffDetails?.tariff?.name })}
                </p>
                <div className={styles.limitBannerActions}>
                  <BpButton
                    contentClassName={'!text-[#00326D] !font-normal'}
                    size={'sm'}
                    theme={'light'}
                    type={'button'}
                    onClick={() => dispatch(setModal({ children: <TariffDetailsModal /> }))}
                  >
                    {t('pages.tariff.buttons.changePlan')}
                  </BpButton>
                  <CardDrop position={'left'} title={t('pages.tariff.buttons.details')}>
                    <div className={styles.cardDropContent}>
                      <div className={styles.cardDropRow}>
                        <p
                          dangerouslySetInnerHTML={{
                            __html: t('pages.tariff.usedCountOfTotalKeywords', {
                              count: tariffDetails?.tariff?.used_keywords_count,
                              total: tariffDetails?.tariff?.max_keywords,
                            }),
                          }}
                          className={styles.cardDropText}
                        />
                      </div>
                      <ul className={styles.cardDropList}>
                        {Object.entries(tariffDetails?.location_details).map(([key, value]) => (
                          <li key={value}>
                            {key}: {value}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </CardDrop>
                </div>
              </div>
            )}
        </div>
      </form>
      <div className={styles.footer}>
        {step > 1 && (
          <BpButton
            contentClassName={styles.backContent}
            size={'xs'}
            theme={'transparent'}
            type={'button'}
            onClick={() => setStep(step - 1)}
          >
            {t('buttons.back')}
          </BpButton>
        )}
        <BpButton
          className={styles.nextButton}
          disabled={disabled}
          loading={createProjectLoading}
          type='button'
          onClick={() => submit()}
        >
          {step !== 3 ? t('buttons.next') : t('buttons.createProject')}
        </BpButton>
      </div>
    </div>
  )
})
